import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import {
  Palette,
  Type,
  Image,
  Save,
  RefreshCw
} from 'lucide-react';

const SettingsPage = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [organization, setOrganization] = useState(null);
  const [branding, setBranding] = useState({
    logo_url: '',
    primary_color: '#2563EB',
    secondary_color: '#0F172A',
    font_family: 'Outfit',
    company_name: '',
    intro_template: '',
    outro_template: ''
  });

  useEffect(() => {
    fetchOrganization();
  }, []);

  const fetchOrganization = async () => {
    try {
      const response = await api.get('/organization');
      setOrganization(response.data);
      setBranding(response.data.branding || branding);
    } catch (error) {
      console.error('Failed to fetch organization:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveBranding = async () => {
    setSaving(true);
    try {
      await api.put('/organization/branding', branding);
      toast.success('Paramètres sauvegardés !');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    setBranding({
      logo_url: '',
      primary_color: '#2563EB',
      secondary_color: '#0F172A',
      font_family: 'Outfit',
      company_name: '',
      intro_template: '',
      outro_template: ''
    });
    toast.info('Paramètres réinitialisés');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto animate-fade-in">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl lg:text-3xl font-semibold text-slate-900 font-['Outfit']">
          Paramètres
        </h1>
        <p className="text-slate-500 mt-1">
          Personnalisez l'apparence de vos formations
        </p>
      </div>

      <div className="space-y-6">
        {/* Branding Card */}
        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5 text-blue-600" />
              Identité visuelle
            </CardTitle>
            <CardDescription>
              Personnalisez les couleurs et le style de vos formations
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Company Name */}
            <div className="space-y-2">
              <Label htmlFor="company-name">Nom de l'entreprise</Label>
              <Input
                id="company-name"
                data-testid="company-name-input"
                value={branding.company_name}
                onChange={(e) => setBranding({ ...branding, company_name: e.target.value })}
                placeholder="Votre entreprise"
              />
            </div>

            {/* Logo URL */}
            <div className="space-y-2">
              <Label htmlFor="logo-url">URL du logo</Label>
              <Input
                id="logo-url"
                data-testid="logo-url-input"
                value={branding.logo_url}
                onChange={(e) => setBranding({ ...branding, logo_url: e.target.value })}
                placeholder="https://..."
              />
              {branding.logo_url && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <img 
                    src={branding.logo_url} 
                    alt="Logo preview"
                    className="max-h-16 object-contain"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </div>

            {/* Colors */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="primary-color">Couleur principale</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    id="primary-color"
                    value={branding.primary_color}
                    onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                    className="w-12 h-10 rounded border border-slate-200 cursor-pointer"
                  />
                  <Input
                    value={branding.primary_color}
                    onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                    className="flex-1"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="secondary-color">Couleur secondaire</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    id="secondary-color"
                    value={branding.secondary_color}
                    onChange={(e) => setBranding({ ...branding, secondary_color: e.target.value })}
                    className="w-12 h-10 rounded border border-slate-200 cursor-pointer"
                  />
                  <Input
                    value={branding.secondary_color}
                    onChange={(e) => setBranding({ ...branding, secondary_color: e.target.value })}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>

            {/* Font */}
            <div className="space-y-2">
              <Label htmlFor="font">Police de caractères</Label>
              <select
                id="font"
                value={branding.font_family}
                onChange={(e) => setBranding({ ...branding, font_family: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-slate-900"
              >
                <option value="Outfit">Outfit (Moderne)</option>
                <option value="Manrope">Manrope (Corporate)</option>
                <option value="Inter">Inter (Neutre)</option>
                <option value="Poppins">Poppins (Amical)</option>
              </select>
            </div>

            {/* Preview */}
            <div className="p-6 rounded-lg border border-slate-200" style={{
              backgroundColor: branding.secondary_color + '10',
              fontFamily: branding.font_family
            }}>
              <h3 className="text-lg font-semibold mb-2" style={{ color: branding.primary_color }}>
                Aperçu du style
              </h3>
              <p className="text-slate-600 mb-4">
                Voici comment apparaîtront vos formations avec ces paramètres.
              </p>
              <button
                className="px-4 py-2 rounded-md text-white font-medium"
                style={{ backgroundColor: branding.primary_color }}
              >
                Bouton exemple
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Templates Card */}
        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Image className="w-5 h-5 text-blue-600" />
              Templates vidéo
            </CardTitle>
            <CardDescription>
              Définissez des intros et outros pour vos vidéos
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="intro">URL du template d'intro</Label>
              <Input
                id="intro"
                data-testid="intro-template-input"
                value={branding.intro_template}
                onChange={(e) => setBranding({ ...branding, intro_template: e.target.value })}
                placeholder="https://... (vidéo MP4)"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="outro">URL du template d'outro</Label>
              <Input
                id="outro"
                data-testid="outro-template-input"
                value={branding.outro_template}
                onChange={(e) => setBranding({ ...branding, outro_template: e.target.value })}
                placeholder="https://... (vidéo MP4)"
              />
            </div>
            
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <strong>Note MVP:</strong> L'application automatique des intros/outros sera disponible dans la V2.
            </div>
          </CardContent>
        </Card>

        {/* Account Info */}
        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle>Informations du compte</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-500">Nom</Label>
                <p className="font-medium text-slate-900">{user?.name}</p>
              </div>
              <div>
                <Label className="text-slate-500">Email</Label>
                <p className="font-medium text-slate-900">{user?.email}</p>
              </div>
              <div>
                <Label className="text-slate-500">Rôle</Label>
                <p className="font-medium text-slate-900 capitalize">{user?.role}</p>
              </div>
              <div>
                <Label className="text-slate-500">Organisation</Label>
                <p className="font-medium text-slate-900">{organization?.name}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={resetToDefaults}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Réinitialiser
          </Button>
          <Button
            data-testid="save-settings-btn"
            onClick={saveBranding}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Sauvegarder
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
